import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { CheckCircle2, XCircle, Loader2, RotateCw, Globe, ArrowRight } from 'lucide-react';

interface ProgressStep {
  step: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  message: string;
}

interface PromotionResult {
  siteId: string;
  oldDomain: string;
  newDomain: string;
  newApiDomain: string;
  nameservers: string[];
  manualSteps: string[];
  /** Non-fatal problems the job hit. Absent on jobs from before this shipped. */
  warnings?: string[];
}

interface StatusResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'dead';
  progress: ProgressStep[];
  result: PromotionResult | null;
  error: string | null;
}

interface PreflightResult {
  domain: string;
  status: 'in_account' | 'subdomain_ok' | 'not_in_account' | 'unregistered' | 'cf_unconfigured' | 'invalid_domain';
  zone_status?: string;
  authoritative_ns?: string[];
  recommendation: string;
}

interface SiteRow {
  id: string;
  name: string;
  domain: string;
}

const WIZARD_BASE = '/admin/platform/site-wizard';

/**
 * Super-admin operator flow to promote a tenant from its temporary
 * `<slug>.rumio.world` subdomain to a real custom domain ("Go Live").
 * The plan gate is bypassed on this path (operators can move any tenant);
 * the customer self-serve flow in the rumio-app enforces the paid-plan gate.
 */
export default function PromoteDomain() {
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [siteId, setSiteId] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');

  const [jobId, setJobId] = useState<string | null>(null);
  const [data, setData] = useState<StatusResponse | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    api.get<{ sites: SiteRow[] }>(`${WIZARD_BASE}/sites`)
      .then((r) => setSites(r.sites.filter((s) => s.domain.endsWith('.rumio.world'))))
      .catch(() => {});
  }, []);

  const runPreflight = useCallback(async () => {
    if (!newDomain.trim()) return;
    setChecking(true);
    setPreflight(null);
    try {
      const r = await api.post<PreflightResult>(`${WIZARD_BASE}/cf-preflight`, { domain: newDomain.trim() });
      setPreflight(r);
    } catch (e: any) {
      setStartError(e.message || 'Preflight failed');
    } finally {
      setChecking(false);
    }
  }, [newDomain]);

  async function handleStart() {
    setStartError('');
    setStarting(true);
    try {
      const r = await api.post<{ jobId: string }>(`${WIZARD_BASE}/promote-domain`, {
        siteId: siteId.trim(),
        newDomain: newDomain.trim(),
      });
      setJobId(r.jobId);
    } catch (e: any) {
      setStartError(e.message || 'Failed to start');
    } finally {
      setStarting(false);
    }
  }

  // Poll progress once a job is running.
  useEffect(() => {
    if (!jobId) return;
    let active = true;
    const poll = async () => {
      try {
        const result = await api.get<StatusResponse>(`${WIZARD_BASE}/promote-status/${jobId}`);
        if (!active) return;
        setData(result);
        if (result.status === 'completed' || result.status === 'failed' || result.status === 'dead') return;
      } catch {
        /* keep polling */
      }
      if (active) setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [jobId, retryNonce]);

  async function handleRetry() {
    if (!jobId) return;
    setRetrying(true);
    try {
      await api.post(`${WIZARD_BASE}/promote-retry/${jobId}`, {});
      setRetryNonce((n) => n + 1);
    } catch {
      /* surfaced via next poll */
    } finally {
      setRetrying(false);
    }
  }

  const isComplete = data?.status === 'completed';
  const isFailed = data?.status === 'failed' || data?.status === 'dead';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-text-primary flex items-center gap-2">
          <Globe className="w-6 h-6 text-sage-600" /> Move a site to its own domain
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Promote a <code className="font-mono">.rumio.world</code> tenant to a real custom domain. The site keeps its
          data, storage, and settings — only the domain changes. Users will be signed out and must log in again on the
          new domain.
        </p>
      </div>

      {!jobId && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-text-primary">Site</span>
            <input
              list="promote-sites"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="the-juliet-detroit"
              className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
            />
            <datalist id="promote-sites">
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name} — {s.domain}</option>
              ))}
            </datalist>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-text-primary">New domain</span>
            <div className="mt-1 flex gap-2">
              <input
                value={newDomain}
                onChange={(e) => { setNewDomain(e.target.value); setPreflight(null); }}
                placeholder="thejulietdetroit.com"
                className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={runPreflight}
                disabled={checking || !newDomain.trim()}
                className="rounded-md border border-stone-300 px-3 py-2 text-sm hover:bg-stone-50 disabled:opacity-50"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
              </button>
            </div>
          </label>

          {preflight && (
            <div className={`rounded-lg px-4 py-3 text-sm ${preflight.status === 'in_account' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
              <p className="font-medium">Preflight: {preflight.status.replace(/_/g, ' ')}</p>
              <p className="mt-1">{preflight.recommendation}</p>
              {preflight.authoritative_ns?.length ? (
                <p className="mt-1 font-mono text-xs">current NS: {preflight.authoritative_ns.join(', ')}</p>
              ) : null}
            </div>
          )}

          <div className="rounded-lg bg-stone-50 border border-stone-200 px-4 py-3 text-xs text-text-secondary">
            The move creates a Cloudflare zone for the new domain and returns two nameservers. Set those at the
            registrar (e.g. Namecheap → Custom DNS). The job then waits for the zone to activate before touching the
            live site — if the nameservers are never set, nothing changes.
          </div>

          {startError && <p className="text-sm text-red-600">{startError}</p>}

          <button
            type="button"
            onClick={handleStart}
            disabled={starting || !siteId.trim() || !newDomain.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-sage-600 hover:bg-sage-700 disabled:bg-sage-400 text-white text-sm font-medium px-4 py-2 transition-colors"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Start move
          </button>
        </div>
      )}

      {jobId && data && (
        <>
          {/* Nameserver call-out (from the zone step) */}
          {data.result?.nameservers?.length ? (
            <div className="rounded-xl bg-sage-50 border border-sage-200 px-5 py-4">
              <p className="text-sm font-semibold text-sage-800">Set these nameservers at your registrar</p>
              <ul className="mt-1 font-mono text-sm text-sage-900">
                {data.result.nameservers.map((ns) => <li key={ns}>{ns}</li>)}
              </ul>
            </div>
          ) : null}

          <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
            {data.progress.map((step) => (
              <div key={step.step} className="flex items-center gap-3 px-5 py-3">
                {step.status === 'done' && <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />}
                {step.status === 'failed' && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                {step.status === 'running' && <Loader2 className="w-5 h-5 text-sage-600 animate-spin flex-shrink-0" />}
                {step.status === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-stone-200 flex-shrink-0" />}
                <span className={`text-sm ${step.status === 'running' ? 'text-text-primary font-medium' : step.status === 'failed' ? 'text-red-600' : 'text-text-secondary'}`}>
                  {step.message}
                </span>
              </div>
            ))}
          </div>

          {data.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{data.error}</div>
          )}

          {isFailed && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex items-center gap-2 rounded-md bg-sage-600 hover:bg-sage-700 disabled:bg-sage-400 text-white text-sm font-medium px-4 py-2"
            >
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
              Retry (e.g. once nameservers are set)
            </button>
          )}

          {isComplete && data.result && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 space-y-3">
              <h2 className="text-lg font-semibold text-green-800">Live on {data.result.newDomain}</h2>
              {/* Only claim the redirect exists when nothing warned about it.
                  This line used to be unconditional and told operators the old
                  host was redirecting even when installing it had failed. */}
              {data.result.warnings?.some((w) => /redirect/i.test(w)) ? (
                <p className="text-sm text-green-700">
                  {data.result.oldDomain} is still serving directly — see the warnings below.
                </p>
              ) : (
                <p className="text-sm text-green-700">
                  {data.result.oldDomain} now 301-redirects to {data.result.newDomain}.
                </p>
              )}
              {data.result.warnings?.length ? (
                <div className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-900">
                    Completed with {data.result.warnings.length} warning
                    {data.result.warnings.length === 1 ? "" : "s"}
                  </p>
                  <ul className="list-disc list-inside text-xs text-amber-800 space-y-1">
                    {data.result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              ) : null}
              {data.result.manualSteps?.length ? (
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-green-800">Manual follow-ups</p>
                  <ul className="list-disc list-inside text-xs text-green-700 space-y-1">
                    {data.result.manualSteps.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}
